import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Snackbar } from 'geniebook-ui/dist';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import UserServices from '@services/GeniebookAPI/UserServices.services';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { ValidationError } from 'yup';
import { useAtomValue } from 'jotai';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const SINGAPORE_COUNTRY_CODE = '65';
const EC_EMAIL_INVALID = '102-04';
const EC_EMAIL_USED = '102-02';
const EC_FORM = '100-04';

const minPhoneLength: { [key: string]: number } = {
  65: 8,
  62: 8,
  84: 8,
  60: 8,
  852: 8,
  1: 10,
  44: 10,
  63: 10,
  66: 8,
};

const maxPhoneLength: { [key: string]: number } = {
  65: 8,
  62: 12,
  84: 11,
  60: 10,
  852: 8,
  1: 10,
  44: 11,
  63: 11,
  66: 10,
};

interface ContactDetails {
  originalEmail: string;
  newEmail: string;
  emailChanged: boolean;
  originalPhone?: string;
  newPhone?: number;
  phoneChanged: boolean;
  countryCode: string;
  phoneMinLength: number;
  phoneMaxLength: number;
}

const AccountForm = () => {
  const navigate = useNavigate();
  const [disableButton, setDisableButton] = useState(true);
  const viewWidth = useViewWidth();
  const getUserDetails = useAtomValue(userDetailQueryAtom);

  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);

  const [contact, setContact] = useState<ContactDetails>({
    originalEmail: '',
    newEmail: '',
    emailChanged: false,
    originalPhone: undefined,
    newPhone: undefined,
    phoneChanged: false,
    countryCode: '',
    phoneMinLength: 0,
    phoneMaxLength: 0,
  });

  useEffect(() => {
    if (getUserDetails.isSuccess) {
      const userData = getUserDetails?.data?.data;

      if (userData) {
        setContact({
          ...contact,
          originalEmail: userData.email,
          originalPhone: userData.phone,
          countryCode: userData.country_code,
          phoneMinLength: minPhoneLength[parseInt(userData.country_code)],
          phoneMaxLength: maxPhoneLength[parseInt(userData.country_code)],
        });
      }
    }
  }, [getUserDetails.data]);

  const [formErrors, setFormErrors] = useState({
    email: '',
    mobile_number: '',
  });

  const phoneValidationSchema = Yup.object().shape({
    newPhone: Yup.string()
      .required('Mobile number is required')
      .matches(/^\d+$/, 'Phone number must consist of digits only')
      .min(
        contact.phoneMinLength,
        contact.countryCode == SINGAPORE_COUNTRY_CODE
          ? `Mobile number must be ${minPhoneLength[SINGAPORE_COUNTRY_CODE]} digits`
          : 'Invalid mobile number',
      )
      .max(
        contact.phoneMaxLength,
        contact.countryCode == SINGAPORE_COUNTRY_CODE
          ? `Mobile number must be ${maxPhoneLength[SINGAPORE_COUNTRY_CODE]} digits`
          : 'Invalid mobile number',
      ),
  });

  const emailValidationSchema = Yup.object().shape({
    newEmail: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
  });

  function handleCancel() {
    navigate('/account-settings');
  }

  const updatePhoneData = useMutation(
    (mobile_number: number) =>
      UserServices.updateUserProfile({
        mobile_number: mobile_number,
        country_code: contact.countryCode,
      }),
    {
      onSuccess(data) {
        if (
          typeof data.data === 'string' &&
          data.data.includes('"error":true')
        ) {
          // in case there's an error from the backend which can't be formatted as an object
          setShowSnackBar(true);
        } else if (data.data.error) {
          switch (data.data.error_code) {
            case EC_FORM:
              setFormErrors({
                ...formErrors,
                mobile_number: data.data.error_msg,
              });
              break;
            default:
              setShowSnackBar(true);
              break;
          }
        } else {
          getUserDetails.refetch();
          navigate('/account-settings');
        }
      },
      onError() {
        setShowSnackBar(true);
      },
    },
  );

  const updateEmailData = useMutation(
    (email: string) => UserServices.updateUserProfile({ email }),
    {
      onSuccess(data) {
        if (data.data.error) {
          switch (data.data.error_code) {
            case EC_EMAIL_INVALID:
              setFormErrors({ ...formErrors, email: data.data.error_msg });
              break;
            case EC_EMAIL_USED:
              setFormErrors({ ...formErrors, email: data.data.error_msg });
              break;
            default:
              setShowSnackBar(true);
              break;
          }
        } else {
          getUserDetails.refetch();
          navigate('/account-settings');
        }
      },
      onError() {
        setShowSnackBar(true);
      },
    },
  );

  const handleSave = async () => {
    // step by step
    // 1. make request to change
    // 2. invalidate react query user detail to make it re-fetch new data
    // 3. navigate to profile

    setFormErrors({
      email: '',
      mobile_number: '',
    });

    try {
      if (contact.phoneChanged) {
        await phoneValidationSchema.validate(
          { newPhone: contact.newPhone },
          { abortEarly: false },
        );

        await updatePhoneData.mutateAsync(contact.newPhone as number);
      }

      if (contact.emailChanged) {
        await emailValidationSchema.validate(
          { newEmail: contact.newEmail },
          { abortEarly: false },
        );

        await updateEmailData.mutateAsync(contact.newEmail);
      }
    } catch (validationErrors: unknown) {
      (validationErrors as ValidationError).inner.forEach(message => {
        if (message.path == 'newPhone') {
          setFormErrors(previousState => ({
            ...previousState,
            mobile_number: message.message,
          }));
        } else {
          setFormErrors(previousState => ({
            ...previousState,
            email: message.message,
          }));
        }
      });
    }
  };

  return (
    <div>
      {showSnackBar ? (
        <Snackbar
          message={'Oops! Something went wrong. Please try again later.'}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowSnackBar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : (
        <></>
      )}
      <div className={'mb-6'}>
        <Input
          mode={'dark'}
          label={'Email'}
          className={'md:max-w-[352px] max-md:max-w-full'}
          value={contact.originalEmail}
          onChange={event => {
            setContact({
              ...contact,
              newEmail: event.target.value,
              originalEmail: event.target.value,
              emailChanged: true,
            });
            setFormErrors({ ...formErrors, email: '' });
            setDisableButton(false);
          }}
          error={formErrors.email ? formErrors.email : ''}
        />
      </div>
      <div
        className={
          'text-md font-medium leading-4 undefined text-neutral-whiteboard'
        }
      >
        Mobile
      </div>
      <div
        className={
          'flex md:max-w-[352px] max-md:max-w-full ' +
          (formErrors.mobile_number ? 'mb-4' : 'mb-10')
        }
      >
        <Select
          darkMode={true}
          label={''}
          className={'max-w-[126px] mr-1.5 [&>div>input]:h-[48px]'}
          value={contact.countryCode}
          placeholder={contact.countryCode as string}
          options={[]}
          onChange={() => {}}
          disabled
        />
        <Input
          mode={'dark'}
          label={''}
          className={'w-full self-end [&>span]:!leading-4 [&>input]:w-full'}
          value={
            isNaN(parseInt(contact.originalPhone as string))
              ? ''
              : contact.originalPhone
          }
          onChange={event => {
            if (String(event.target.value).length <= contact.phoneMaxLength) {
              setContact({
                ...contact,
                newPhone: parseInt(event.target.value, 10),
                originalPhone: String(parseInt(event.target.value, 10)),
                phoneChanged: true,
              });
            }
            setFormErrors({ ...formErrors, mobile_number: '' });
            setDisableButton(false);
          }}
          onKeyDown={event => {
            if (event.key === 'Backspace') {
              setContact({
                ...contact,
                newPhone: parseInt(String(contact.newPhone).slice(0, -1)),
                originalPhone: String(contact.originalPhone).slice(0, -1),
                phoneChanged: true,
              });
            }
          }}
          error={formErrors.mobile_number ? formErrors.mobile_number : ''}
        />
      </div>
      <div className={'flex'}>
        <Button
          variant={'primary'}
          className={'font-black max-md:!w-full max-md:!max-w-full'}
          onClick={handleSave}
          disabled={disableButton}
        >
          Save
        </Button>
        <Button
          variant={'secondary'}
          className={'max-lg:hidden'}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AccountForm;
